import type { OrderSource } from '../../components/customer/CustomerChips.tsx';
import axiosInstance from '../../core/axiosConfig.ts';

/**
 * Shabbat meals, the host's side. The contract is the backend's API.md
 * ("Host panel endpoints"): a restaurant login reaches its own venue only,
 * an admin any venue that hosts Shabbat meals. The server computes every
 * amount; the panel only shows them.
 */

export type MealType = 'FRIDAY_DINNER' | 'SHABBAT_LUNCH';
export type MealStatus = 'OPEN' | 'FULL' | 'CLOSED';
export type RegistrationStatus = 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELLED' | 'EXPIRED';
export type RefundStatus = 'PENDING' | 'AUTHORIZED' | 'CAPTURED' | 'FAILED';

export type ShabbatVenue = {
    id: number;
    name: string;
    imageUrl: string | null;
    address: string | null;
    cityName: string | null;
    currency: string;
    timeZone: string;
    shabbatOnly: boolean;
    /**
     * The venue's "Hosts Shabbat meals" switch. Admins also get venues whose
     * switch is now off but that have had meals (false here): their history
     * is readable, no meal can be added or edited there (25 Oct 2026).
     */
    hostsShabbat: boolean;
};

export type VendorMeal = {
    id: number;
    locationId: number;
    type: MealType;
    startsAt: string;
    date: string;
    startTime: string;
    timeZone: string;
    menu: string | null;
    adultPrice: number;
    childPrice: number;
    currency: string;
    seats: number;
    seatsTaken: number;
    seatsLeft: number;
    status: MealStatus;
    closesAt: string | null;
    closesAtDate: string | null;
    closesAtTime: string | null;
    closesAtGuests: number | null;
    cancelUntil: string | null;
    acceptDonations: boolean;
    guests: number;
    adults: number;
    children: number;
    registrations: number;
    pendingGuests: number;
    collected: number;
    donations: number;
    donationsCount: number;
    /** The venue's name (backend from 23 Oct 2026). */
    locationName?: string | null;
};

/** One meal a registration covers (a guest can register for Friday dinner and Shabbat lunch at once). */
export type RegistrationMealLine = {
    mealId: number;
    type: MealType;
    date: string;
    startTime: string;
    adults: number;
    children: number;
    mealAmount: number;
    status: 'ACTIVE' | 'CANCELLED';
};

/**
 * A registration on one meal's list. adults, children and mealAmount are
 * THIS meal's; donation and totalAmount are the whole payment, which may
 * cover two meals. `combined` and `meals` are missing from a backend older
 * than the combined registration (21 Oct 2026).
 */
export type VendorRegistration = {
    id: number;
    reference: string;
    bookedBy: string;
    phone: string;
    adults: number;
    children: number;
    guestNames: string[];
    mealAmount: number;
    donation: number;
    totalAmount: number;
    currency: string;
    status: RegistrationStatus;
    createdAt: string;
    cancelledAt: string | null;
    cancelledBy: 'GUEST' | 'HOST' | 'MEAL_DELETED' | null;
    refundStatus: RefundStatus | null;
    refundAmount: number | null;
    combined?: boolean;
    meals?: RegistrationMealLine[];
    /** This meal's share of the payment: its food, plus the donation when this is the registration's first meal. */
    paidForThisMeal?: number;
    /** The whole payment (all meals + donation). */
    paymentTotal?: number;
    /** Where it was placed; null for registrations made before 21 Oct 2026. */
    src?: OrderSource | null;
    /** Earlier confirmed registrations on Vello, any venue (0 = NC). Admin only: absent for a host login (Ariel, 25 Oct 2026). */
    prev?: number | null;
    /** The meal this row is listed under, and its venue (backend from 23 Oct 2026). */
    mealId?: number;
    venueId?: number;
    venueName?: string | null;
};

export type ShabbatSummary = {
    friday: string;
    saturday: string;
    currency: string;
    meals: number;
    mealsFull: number;
    guests: number;
    registrations: number;
    seatsLeft: number;
    collected: number;
    donations: number;
    donationsCount: number;
};

export type MealBody = {
    locationId: number;
    type: MealType;
    date: string;
    startTime: string;
    menu: string;
    seats: number;
    adultPrice: number;
    childPrice: number;
    closesAtDate: string | null;
    closesAtTime: string | null;
    closesAtGuests: number | null;
    acceptDonations: boolean;
};

/** Query parameters for the host lists: none when neither is given. */
const params = (locationId: number | null, from?: string | null) => {
    const p: Record<string, string | number> = {};
    if (locationId) p.locationId = locationId;
    if (from) p.from = from;
    return Object.keys(p).length ? p : undefined;
};

export default class ShabbatService {
    async venues(): Promise<ShabbatVenue[]> {
        const response = await axiosInstance.get('/vendor/shabbat/venues');
        return Array.isArray(response.data) ? response.data : [];
    }

    /** locationId null: every venue added up (admins), the login's own venue (restaurants). */
    async summary(locationId: number | null): Promise<ShabbatSummary> {
        const response = await axiosInstance.get('/vendor/shabbat/summary', { params: locationId ? { locationId } : undefined });
        return response.data;
    }

    /**
     * locationId null: every venue's meals (admins), the login's own (restaurants).
     * from (YYYY-MM-DD): meals from that day on instead of from this Shabbat.
     */
    async meals(locationId: number | null, from?: string | null): Promise<VendorMeal[]> {
        const response = await axiosInstance.get('/vendor/shabbat/meals', { params: params(locationId, from) });
        return Array.isArray(response.data) ? response.data : [];
    }

    /**
     * Every meal in view from this Shabbat on, and one row per registration
     * per meal (each row names its meal and venue). locationId null: every
     * venue for an admin, the login's own for a restaurant.
     */
    async registrationsInView(locationId: number | null, from?: string | null): Promise<{ meals: VendorMeal[]; registrations: VendorRegistration[] }> {
        const response = await axiosInstance.get('/vendor/shabbat/registrations', { params: params(locationId, from) });
        return { meals: response.data?.meals ?? [], registrations: response.data?.registrations ?? [] };
    }

    async createMeal(body: MealBody): Promise<VendorMeal> {
        const response = await axiosInstance.post('/vendor/shabbat/meals', body);
        return response.data;
    }

    async updateMeal(id: number, body: MealBody): Promise<VendorMeal> {
        const response = await axiosInstance.put(`/vendor/shabbat/meals/${id}`, body);
        return response.data;
    }

    async deleteMeal(id: number): Promise<{ cancelledRegistrations: number; refunded: number }> {
        const response = await axiosInstance.delete(`/vendor/shabbat/meals/${id}`);
        return response.data ?? { cancelledRegistrations: 0, refunded: 0 };
    }

    async registrations(mealId: number): Promise<{ meal: VendorMeal; registrations: VendorRegistration[] }> {
        const response = await axiosInstance.get(`/vendor/shabbat/meals/${mealId}/registrations`);
        return response.data;
    }

    /**
     * Cancels a registration and refunds it. With `mealId`, only that meal is
     * cancelled (its food refunded); the registration stays for its other
     * meal. Cancelling its last meal cancels the whole registration.
     */
    async cancelRegistration(id: number, mealId?: number): Promise<VendorRegistration> {
        const response = await axiosInstance.post(`/vendor/shabbat/registrations/${id}/cancel`, undefined,
            mealId ? { params: { mealId } } : undefined);
        return response.data;
    }
}

/** The server's message for a failed call, or a fallback. */
export const errorText = (e: any, fallback: string): string => {
    const data = e?.response?.data;
    if (data && typeof data === 'object' && typeof data.error === 'string') return data.error;
    if (typeof data === 'string' && data.length < 200) return data;
    return fallback;
};

// ---------------------------------------------------------------- host logins

/** A venue's login, as an admin sees it (`/admin/shabbat/venues/{id}/hosts`). */
export type ShabbatHost = {
    id: number;
    locationId: number;
    email: string;
    name: string | null;
    phoneNumber: string | null;
    blocked: boolean;
};

/** A host login just made, or a fresh link: `setPasswordLink` is for the admin to pass on. */
export type ShabbatHostInvite = {
    host: ShabbatHost;
    emailSent: boolean;
    setPasswordLink: string | null;
};

/** Admins only: the venue's host login(s). */
export async function getShabbatHosts(locationId: number): Promise<ShabbatHost[]> {
    const response = await axiosInstance.get(`/admin/shabbat/venues/${locationId}/hosts`);
    return response.data;
}

/** Admins only: makes the venue's host login and emails it; one per venue. */
export async function inviteShabbatHost(
    locationId: number,
    body: { email: string; name: string; phoneNumber?: string },
): Promise<ShabbatHostInvite> {
    const response = await axiosInstance.post(`/admin/shabbat/venues/${locationId}/hosts`, body);
    return response.data;
}

/** Admins only: a fresh one-time set-password link for one of the venue's logins. */
export async function shabbatHostPasswordLink(locationId: number, hostId: number): Promise<ShabbatHostInvite> {
    const response = await axiosInstance.post(`/admin/shabbat/venues/${locationId}/hosts/${hostId}/password-link`);
    return response.data;
}
